import { useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { HelpCircle, GraduationCap, X } from "lucide-react";
import TutorialViewer from "./TutorialViewer";
import { useLang } from "../../i18n/LangContext";
import { ROUTE_TO_TUTORIAL } from "../../tutorials/labels";

/**
 * Floating "?" bubble anchored bottom-right.  On click it opens the tutorial
 * matched to the current route (via ROUTE_TO_TUTORIAL).  If there's no direct
 * match, it opens the general "start-here" tutorial.
 *
 * The bubble is hidden on public/marketing pages (/, /landing, /login, /blog)
 * and on the tutorial hub itself to avoid recursion.
 */
const HIDDEN_PREFIXES = ["/landing", "/login", "/signup", "/blog", "/magic-login", "/auth", "/tutorials"];

export default function FloatingHelpButton() {
  const location = useLocation();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const tutorialId = useMemo(() => {
    // Longest-prefix match so `/smith/ai-search/settings` resolves to
    // the AI Search tutorial even though its exact route isn't listed.
    const path = location.pathname;
    if (ROUTE_TO_TUTORIAL[path]) return ROUTE_TO_TUTORIAL[path];
    const matches = Object.keys(ROUTE_TO_TUTORIAL)
      .filter((k) => path.startsWith(k))
      .sort((a, b) => b.length - a.length);
    return matches[0] ? ROUTE_TO_TUTORIAL[matches[0]] : "start-here";
  }, [location.pathname]);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))
              || location.pathname === "/";
  if (hidden || dismissed) return null;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2" data-testid="floating-help-widget">
        <Link
          to="/tutorials"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md hover:shadow-lg text-xs font-medium text-slate-700 dark:text-slate-200 transition"
          data-testid="floating-help-hub-link">
          <GraduationCap className="w-4 h-4 text-violet-600" />
          {t("tut.nav.title")}
        </Link>
        <button
          onClick={() => setOpen(true)}
          className="w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-700 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-105"
          data-testid="floating-help-button"
          aria-label={t("tut.help.float")}
          title={t("tut.help.tooltip")}>
          <HelpCircle className="w-6 h-6" />
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-slate-700 text-white text-xs flex items-center justify-center hover:bg-slate-900"
          data-testid="floating-help-dismiss"
          aria-label="Dismiss">
          <X className="w-3 h-3" />
        </button>
      </div>
      <TutorialViewer
        tutorialId={tutorialId}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
